// Connection Readiness V2 — Adaptive branching with 3 named branches + 7 archetypes
// Internal: connection-readiness-v2

export const INTENT_OPTIONS = [
  { id: 'ltr', label: 'Serious long-term relationship', summary: 'a serious long-term relationship' },
  { id: 'intentional_dating', label: 'Intentional dating', summary: 'intentional dating' },
  { id: 'casual', label: 'Casual connection', summary: 'casual connection' },
  { id: 'companionship', label: 'Companionship', summary: 'companionship' },
  { id: 'open_flexible', label: 'Open / flexible connection', summary: 'open or flexible connection' },
  { id: 'supplemental', label: 'Supplemental connection', summary: 'supplemental connection' },
  { id: 'exploration', label: 'Exploration / figuring things out', summary: 'exploration' },
  { id: 'complicated', label: "Something else / it's complicated", summary: 'something a little more complex' },
];

export const ARCHETYPES = {
  ready: {
    label: 'Ready',
    narrative:
      "You appear realistically aligned with the kind of connection you're seeking. That doesn't mean perfect. It means your expectations, energy, and availability seem to be moving in the same direction.",
    bullets: ['Aligned intent', 'Available bandwidth', 'Consistency', 'Honesty'],
    lowReadiness: false,
  },
  open_but_guarded: {
    label: 'Open, But Guarded',
    narrative:
      "You seem open to connection, but some self-protection is still active. That's not a flaw. It just means pacing, trust, and emotional safety may matter more right now.",
    bullets: ['Cautious optimism', 'Trust sensitivity', 'Pacing matters'],
    lowReadiness: false,
  },
  burned_out: {
    label: 'Burned Out',
    narrative:
      "You may still want connection, but the process itself appears to be draining your energy. That's different from not wanting connection.",
    bullets: ['Dating fatigue', 'Emotional depletion', 'Low bandwidth'],
    lowReadiness: true,
  },
  rebuilding: {
    label: 'Rebuilding',
    narrative:
      "You may still be healing, recalibrating, or rebuilding trust after prior experiences. That's a valid place to be.",
    bullets: ['Healing', 'Recalibration', 'Reflection'],
    lowReadiness: true,
  },
  interested_but_inconsistent: {
    label: 'Interested, But Inconsistent',
    narrative:
      "Connection interests you, but your availability, bandwidth, or behavior may not fully match that interest right now.",
    bullets: ['Curiosity', 'Inconsistent availability', 'Limited bandwidth'],
    lowReadiness: true,
  },
  mixed_signals: {
    label: 'Mixed Signals',
    narrative:
      "Part of you seems interested in connection. Another part seems uncertain, conflicted, or unclear. That tension is useful to understand.",
    bullets: ['Uncertainty', 'Internal contradiction', 'Unclear intent'],
    lowReadiness: true,
  },
  structurally_unclear: {
    label: 'Structurally Unclear',
    narrative:
      "Your interest in connection may be real, but the structure surrounding that connection still appears somewhat unclear. That's worth sorting through before bringing someone else into it.",
    bullets: ['Unclear structure', 'Boundary ambiguity', 'Relational visibility gaps'],
    lowReadiness: true,
  },
};

// --- UNIVERSAL QUESTIONS (asked of every user) ---
export const UNIVERSAL_QUESTIONS = [
  {
    id: 'u1',
    type: 'choice',
    prompt:
      'If the kind of connection you say you want became available tomorrow, what feels most true?',
    options: [
      { label: "I'd genuinely be open", scores: { ready: 5 } },
      { label: "I'd move carefully", scores: { open_but_guarded: 4 } },
      { label: "I'd hesitate", scores: { open_but_guarded: 2, rebuilding: 2 } },
      { label: 'Depends on what it required', scores: { burned_out: 3, interested_but_inconsistent: 1 } },
      { label: 'I like the idea more than the reality', scores: { interested_but_inconsistent: 4 } },
      { label: "I honestly don't know", scores: { mixed_signals: 4 } },
    ],
  },
  {
    id: 'u2',
    type: 'choice',
    prompt: 'What feels hardest right now?',
    options: [
      { label: 'Trusting people', scores: { open_but_guarded: 3, rebuilding: 2 } },
      { label: 'Putting in effort', scores: { burned_out: 4 } },
      { label: 'Being honest about what I want', scores: { mixed_signals: 3, structurally_unclear: 1 } },
      { label: 'Knowing what I actually want', scores: { mixed_signals: 4 } },
      { label: 'Making time', scores: { interested_but_inconsistent: 4 } },
      { label: 'Starting over', scores: { rebuilding: 4 } },
    ],
  },
  {
    id: 'u3',
    type: 'slider',
    prompt: 'How much energy do you realistically have for connection right now?',
    min: 1,
    max: 5,
    labels: ['Low', 'High'],
    scoring: [
      { value: 1, scores: { burned_out: 4 } },
      { value: 2, scores: { burned_out: 3 } },
      { value: 3, scores: { open_but_guarded: 2, mixed_signals: 1 } },
      { value: 4, scores: { ready: 3 } },
      { value: 5, scores: { ready: 5 } },
    ],
  },
  {
    id: 'u4',
    type: 'choice',
    prompt: 'How consistent could you realistically be right now?',
    options: [
      { label: 'Very consistent', scores: { ready: 4 } },
      { label: 'Mostly consistent', scores: { ready: 2, open_but_guarded: 1 } },
      { label: 'Depends on the situation', scores: { interested_but_inconsistent: 3 } },
      { label: 'Honestly inconsistent', scores: { interested_but_inconsistent: 5 } },
      { label: "I'm not sure", scores: { mixed_signals: 3 } },
    ],
  },
  {
    id: 'u5',
    type: 'choice',
    prompt: "Be honest: what's driving your interest in connection right now?",
    options: [
      { label: 'I genuinely want connection', scores: { ready: 4 } },
      { label: 'I miss intimacy', scores: { open_but_guarded: 2, ready: 1 } },
      { label: 'I feel lonely', scores: { mixed_signals: 2, interested_but_inconsistent: 1 } },
      { label: "I'm curious", scores: { interested_but_inconsistent: 3 } },
      { label: 'I feel like I should be trying', scores: { burned_out: 3, mixed_signals: 2 } },
      { label: "I honestly don't know", scores: { mixed_signals: 4 } },
    ],
  },
  {
    id: 'u6',
    type: 'choice',
    prompt: 'Can you clearly communicate what you can and cannot offer right now?',
    options: [
      { label: 'Very clearly', scores: { ready: 4 } },
      { label: 'Mostly', scores: { ready: 2, open_but_guarded: 1 } },
      { label: 'Somewhat', scores: { open_but_guarded: 2, mixed_signals: 1 } },
      { label: 'Not really', scores: { mixed_signals: 3, structurally_unclear: 1 } },
      { label: "Honestly, I'm still figuring that out", scores: { mixed_signals: 4, structurally_unclear: 1 } },
    ],
  },
];

// --- BRANCH A: Casual / Exploration readiness ---
const BRANCH_A = [
  {
    id: 'a1',
    type: 'choice',
    prompt:
      'If your intentions changed after meeting someone, how likely are you to say so clearly?',
    options: [
      { label: 'Very likely', scores: { ready: 4 } },
      { label: 'Probably', scores: { ready: 2, open_but_guarded: 1 } },
      { label: 'Depends', scores: { mixed_signals: 2 } },
      { label: 'Probably not', scores: { mixed_signals: 3, structurally_unclear: 2 } },
      { label: 'Not sure', scores: { mixed_signals: 3, structurally_unclear: 1 } },
    ],
  },
  {
    id: 'a2',
    type: 'choice',
    prompt: 'How comfortable are you being honest when expectations may not match?',
    options: [
      { label: 'Very comfortable', scores: { ready: 4 } },
      { label: 'Mostly', scores: { ready: 2, open_but_guarded: 1 } },
      { label: 'Somewhat', scores: { open_but_guarded: 2 } },
      { label: 'Not very', scores: { mixed_signals: 3, structurally_unclear: 2 } },
      { label: 'Depends', scores: { mixed_signals: 2 } },
    ],
  },
];

// --- BRANCH B: Structural / Relational complexity ---
const BRANCH_B = [
  {
    id: 'b1',
    type: 'choice',
    prompt: "If other people are part of this picture, how clear is everyone on what's happening?",
    options: [
      { label: 'Everyone involved is fully aware and aligned', scores: { ready: 4 } },
      { label: 'Some things are understood, but not fully explicit', scores: { structurally_unclear: 4 } },
      { label: "It's complicated", scores: { structurally_unclear: 5 } },
      { label: 'Not everyone has full visibility', scores: { structurally_unclear: 5, mixed_signals: 1 } },
      { label: 'Not applicable', scores: {} },
    ],
  },
  {
    id: 'b2',
    type: 'choice',
    prompt: 'How clearly do you understand the boundaries around this situation?',
    options: [
      { label: 'Very clearly', scores: { ready: 3 } },
      { label: 'Mostly clearly', scores: { ready: 2, open_but_guarded: 1 } },
      { label: 'Somewhat', scores: { structurally_unclear: 2, open_but_guarded: 1 } },
      { label: 'Not clearly', scores: { structurally_unclear: 4 } },
      { label: 'Still figuring that out', scores: { structurally_unclear: 5, mixed_signals: 1 } },
    ],
  },
  {
    id: 'b3',
    type: 'choice',
    prompt: 'Could your current situation create confusion or emotional risk for someone else?',
    options: [
      { label: 'No', scores: { ready: 3 } },
      { label: 'Possibly', scores: { structurally_unclear: 3 } },
      { label: 'Probably', scores: { structurally_unclear: 5 } },
      { label: "I'm not sure", scores: { structurally_unclear: 3, mixed_signals: 1 } },
      { label: 'It depends', scores: { structurally_unclear: 2 } },
    ],
  },
];

// --- BRANCH C: Longer-term / partnership ---
const BRANCH_C = [
  {
    id: 'c1',
    type: 'slider',
    prompt: 'How emotionally available do you feel for meaningful connection right now?',
    min: 1,
    max: 5,
    labels: ['Low', 'High'],
    scoring: [
      { value: 1, scores: { burned_out: 3, rebuilding: 2 } },
      { value: 2, scores: { rebuilding: 2 } },
      { value: 3, scores: { open_but_guarded: 2 } },
      { value: 4, scores: { ready: 3 } },
      { value: 5, scores: { ready: 5 } },
    ],
  },
  {
    id: 'c2',
    type: 'choice',
    prompt: 'How comfortable are you with emotional vulnerability?',
    options: [
      { label: 'Very comfortable', scores: { ready: 4 } },
      { label: 'Mostly', scores: { ready: 2, open_but_guarded: 1 } },
      { label: 'Somewhat', scores: { open_but_guarded: 2 } },
      { label: 'Not very', scores: { rebuilding: 3, open_but_guarded: 1 } },
      { label: 'Depends', scores: { mixed_signals: 2 } },
    ],
  },
];

const BRANCH_A_TRIGGERS = new Set(['casual', 'exploration']);
const BRANCH_B_TRIGGERS = new Set(['open_flexible', 'supplemental', 'complicated']);
const BRANCH_C_TRIGGERS = new Set(['ltr', 'intentional_dating', 'companionship']);

const anyMatch = (selected, triggers) => selected.some((id) => triggers.has(id));

// Adaptive branch derivation: returns ordered list of relevant branch questions.
// User receives ONLY branches triggered by their intent selection.
export const getBranchQuestions = (selectedIntents) => {
  const out = [];
  if (anyMatch(selectedIntents, BRANCH_A_TRIGGERS)) out.push(...BRANCH_A);
  if (anyMatch(selectedIntents, BRANCH_B_TRIGGERS)) out.push(...BRANCH_B);
  if (anyMatch(selectedIntents, BRANCH_C_TRIGGERS)) out.push(...BRANCH_C);
  return out;
};

const accumulate = (totals, scores) => {
  if (!scores) return;
  Object.entries(scores).forEach(([k, v]) => {
    totals[k] = (totals[k] || 0) + v;
  });
};

export const calculateResult = ({ universalAnswers, branchQuestions, branchAnswers }) => {
  const totals = {};
  Object.keys(ARCHETYPES).forEach((k) => (totals[k] = 0));

  UNIVERSAL_QUESTIONS.forEach((q, i) => {
    const ans = universalAnswers[i];
    if (ans === null || ans === undefined) return;
    if (q.type === 'choice') accumulate(totals, q.options[ans]?.scores);
    else if (q.type === 'slider') accumulate(totals, q.scoring.find((s) => s.value === ans)?.scores);
  });

  branchQuestions.forEach((q, i) => {
    const ans = branchAnswers[i];
    if (ans === null || ans === undefined) return;
    if (q.type === 'choice') accumulate(totals, q.options[ans]?.scores);
    else if (q.type === 'slider') accumulate(totals, q.scoring.find((s) => s.value === ans)?.scores);
  });

  const winner = Object.entries(totals).reduce((a, b) => (b[1] > a[1] ? b : a));
  return { archetype: winner[0], scores: totals, result: ARCHETYPES[winner[0]] };
};

const joinList = (items) => {
  if (items.length === 0) return 'connection';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export const getPersonalizedHeadline = (archetypeKey, selectedIntents) => {
  const summaries = selectedIntents
    .map((id) => INTENT_OPTIONS.find((o) => o.id === id)?.summary)
    .filter(Boolean);
  const phrase = joinList(summaries);

  const templates = {
    ready: `You appear realistically aligned with ${phrase}.`,
    open_but_guarded: `You seem open to ${phrase}, but pacing and trust may matter more than speed right now.`,
    burned_out: `You may still want ${phrase}, but the process itself appears to be draining your energy.`,
    rebuilding: `You may be drawn to ${phrase}, but recalibration still seems active.`,
    interested_but_inconsistent: `You appear interested in ${phrase}, but availability or consistency may not fully match that interest yet.`,
    mixed_signals: `Part of you appears interested in ${phrase}. Another part may still be uncertain.`,
    structurally_unclear: `Your interest in ${phrase} seems real, but structural clarity may still need sorting.`,
  };
  return templates[archetypeKey] || templates.mixed_signals;
};
